import { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { MobileShell, BottomNav } from '../components/MobileShell';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Send, LifeBuoy, Loader2, ChevronLeft, RotateCcw, Sparkles, Languages } from 'lucide-react';

const CHUY_AVATAR = '/chuy.png';

const QUICK_PROMPTS_ES = [
  '¿Qué hacer hoy en Playa?',
  '¿Mejor playa para snorkel?',
  'Recomiéndame un cenote',
  'Tips de seguridad',
];
const QUICK_PROMPTS_EN = [
  'What to do in Playa today?',
  'Best beach for snorkeling?',
  'Recommend a cenote',
  'Safety tips',
];

export default function ChuyChatPage() {
  const { user, api } = useAuth();
  const { language, t } = useLanguage();
  const [messages, setMessages] = useState([]);
  const [extracted, setExtracted] = useState(null);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const scrollRef = useRef(null);

  // Load conversation history from backend; greet only if empty
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!user) {
        setMessages([{ role: 'assistant', text: t('chuy.greeting') }]);
        return;
      }
      try {
        const [{ data: hist }, { data: ext }] = await Promise.all([
          api().get('/chuy/history'),
          api().get('/chuy/extracted'),
        ]);
        if (cancelled) return;
        if (hist?.items?.length) {
          setMessages(hist.items.map((m) => ({ role: m.role, text: m.text })));
        } else {
          setMessages([{ role: 'assistant', text: t('chuy.greeting') }]);
        }
        if (ext && Object.keys(ext).length) setExtracted(ext);
      } catch {
        setMessages([{ role: 'assistant', text: t('chuy.greeting') }]);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, language]);

  const handleClearChat = async () => {
    if (!user) return;
    const confirmed = window.confirm(
      language === 'en'
        ? 'Clear conversation with Chuy?'
        : '¿Borrar la conversación con Chuy?'
    );
    if (!confirmed) return;
    try {
      await api().delete('/chuy/history');
      setMessages([{ role: 'assistant', text: t('chuy.greeting') }]);
      setExtracted(null);
    } catch {
      // ignore
    }
  };

  // Auto scroll to bottom on new messages
  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages, loading]);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || loading) return;
    setInput('');
    setMessages((m) => [...m, { role: 'user', text: msg }]);
    setLoading(true);
    try {
      const { data } = await api().post('/chuy/chat', { message: msg, language });
      setMessages((m) => [...m, { role: 'assistant', text: data.response || data.message || '...' }]);
      if (data.extracted && Object.keys(data.extracted).length) {
        setExtracted((prev) => ({ ...(prev || {}), ...data.extracted }));
      }
    } catch (e) {
      setMessages((m) => [
        ...m,
        { role: 'assistant', text: language === 'en' ? 'Sorry, Chuy is busy. Try again in a moment.' : 'Perdón, Chuy anda ocupado. Intenta en un momento.' },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const prompts = language === 'en' ? QUICK_PROMPTS_EN : QUICK_PROMPTS_ES;

  return (
    <MobileShell withBottomNav={false} className="!pb-0">
      {/* Full-height flex column. dvh handles iOS viewport changes (URL bar). */}
      <div
        className="flex flex-col"
        style={{
          height: '100dvh',
          paddingTop: 'env(safe-area-inset-top)',
          paddingBottom: 'calc(4rem + env(safe-area-inset-bottom))', // bottom-nav clearance
        }}
      >
        {/* Header */}
        <header className="px-4 py-3 flex items-center gap-3 border-b border-border bg-background/95 backdrop-blur-md shrink-0">
          <Link
            to="/dashboard"
            data-testid="chuy-chat-back"
            className="w-9 h-9 rounded-full flex items-center justify-center text-muted-foreground hover:bg-muted shrink-0"
          >
            <ChevronLeft size={20} />
          </Link>
          <img
            src={CHUY_AVATAR}
            alt="Chuy"
            className="w-14 h-14 rounded-full object-cover object-top border-2 border-caribbean shrink-0 bg-caribbean/10"
          />
          <div className="flex-1 min-w-0">
            <p className="font-heading font-bold text-base leading-tight">Chuy</p>
            <p className="text-[11px] text-caribbean font-medium flex items-center gap-1">
              <span className="inline-block w-1.5 h-1.5 rounded-full bg-emerald-500" />
              {language === 'en' ? 'Online · Mexican concierge' : 'En línea · Concierge mexicano'}
            </p>
          </div>
          <Link
            to="/chuy-help"
            data-testid="chuy-help-link"
            className="w-9 h-9 rounded-full flex items-center justify-center text-muted-foreground hover:bg-muted shrink-0"
            title={language === 'en' ? 'Chuy Help' : 'Chuy Ayuda'}
          >
            <LifeBuoy size={18} />
          </Link>
          <button
            onClick={handleClearChat}
            data-testid="chuy-clear-chat"
            className="w-9 h-9 rounded-full flex items-center justify-center text-muted-foreground hover:bg-muted shrink-0"
            title={language === 'en' ? 'Clear conversation' : 'Borrar conversación'}
            aria-label={language === 'en' ? 'Clear conversation' : 'Borrar conversación'}
          >
            <RotateCcw size={16} />
          </button>
        </header>

        {/* Scrollable messages area */}
        <div
          ref={scrollRef}
          className="flex-1 min-h-0 overflow-y-auto px-4 py-4 space-y-3"
          data-testid="chuy-chat-messages"
        >
          {messages.map((m, i) => (
            <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
              {m.role === 'assistant' && (
                <img src={CHUY_AVATAR} alt="" className="w-7 h-7 rounded-full mr-2 shrink-0 mt-auto object-cover object-top bg-caribbean/10" />
              )}
              <div
                className={`max-w-[80%] rounded-2xl px-3.5 py-2.5 text-sm leading-relaxed whitespace-pre-wrap ${
                  m.role === 'user'
                    ? 'bg-caribbean text-white rounded-br-sm'
                    : 'bg-muted text-foreground rounded-bl-sm'
                }`}
              >
                {m.text}
              </div>
            </div>
          ))}
          {loading && (
            <div className="flex items-center gap-2">
              <img src={CHUY_AVATAR} alt="" className="w-7 h-7 rounded-full object-cover object-top bg-caribbean/10" />
              <div className="bg-muted rounded-2xl rounded-bl-sm px-4 py-3 flex gap-1">
                <span className="w-1.5 h-1.5 rounded-full bg-muted-foreground animate-bounce" />
                <span className="w-1.5 h-1.5 rounded-full bg-muted-foreground animate-bounce" style={{ animationDelay: '120ms' }} />
                <span className="w-1.5 h-1.5 rounded-full bg-muted-foreground animate-bounce" style={{ animationDelay: '240ms' }} />
              </div>
            </div>
          )}
        </div>

        {/* Quick prompts (first message only) */}
        {messages.length <= 1 && (
          <div className="px-4 pb-2 flex gap-2 overflow-x-auto scrollbar-hide shrink-0">
            {prompts.map((p) => (
              <button
                key={p}
                data-testid="chuy-quick-prompt"
                onClick={() => send(p)}
                className="shrink-0 text-xs px-3 h-8 rounded-full bg-muted hover:bg-caribbean/10 hover:text-caribbean transition-colors"
              >
                {p}
              </button>
            ))}
          </div>
        )}

        {/* Sticky CTA — appears once we have enough trip data. */}
        {extracted && (extracted.location && (extracted.people_count || extracted.date || extracted.category)) && (() => {
          const params = new URLSearchParams();
          if (extracted.category) params.set('category', extracted.category);
          if (extracted.location) params.set('location', extracted.location);
          if (extracted.date) params.set('date', extracted.date);
          if (extracted.time) params.set('time', extracted.time);
          if (extracted.people_count) params.set('people_count', String(extracted.people_count));
          if (extracted.budget_min) params.set('budget_min', String(extracted.budget_min));
          if (extracted.budget_max) params.set('budget_max', String(extracted.budget_max));
          if (extracted.notes) params.set('notes', extracted.notes);
          if (extracted.currency) params.set('currency', extracted.currency);

          // Build chip summary
          const chips = [];
          if (extracted.category) chips.push(extracted.category);
          if (extracted.location) chips.push(extracted.location.replace(/_/g, ' '));
          if (extracted.people_count) chips.push(`${extracted.people_count}p`);
          if (extracted.date) chips.push(extracted.date);
          if (extracted.budget_max) {
            const cur = extracted.currency || 'USD';
            chips.push(`$${Math.round(extracted.budget_max).toLocaleString()} ${cur}`);
          }

          return (
            <div className="px-3 pb-2 shrink-0">
              <Link
                to={`/dashboard/create-request?${params.toString()}`}
                data-testid="chuy-cta-quote"
                className="flex items-center gap-2 rounded-2xl bg-gradient-to-r from-caribbean to-caribbean/80 hover:from-caribbean/90 hover:to-caribbean/70 text-white px-4 py-3 shadow-lg active:scale-[0.98] transition-all"
              >
                <Sparkles size={18} className="shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-bold leading-tight">
                    {language === 'en' ? 'Get quotes on BidTrip' : 'Pedir cotizaciones en BidTrip'}
                  </p>
                  <p className="text-[11px] opacity-90 truncate">
                    {chips.length > 0
                      ? chips.join(' · ')
                      : (language === 'en' ? 'Pre-filled from your chat' : 'Llenado con tu plática')}
                  </p>
                </div>
                <span className="text-white/90 text-sm font-bold">→</span>
              </Link>
            </div>
          );
        })()}

        {/* Input dock */}
        <div className="border-t border-border bg-background/95 backdrop-blur-md px-3 py-2.5 shrink-0">
          <form
            onSubmit={(e) => { e.preventDefault(); send(); }}
            className="flex items-center gap-2"
            data-testid="chuy-chat-form"
          >
            <Link
              to="/translator"
              data-testid="chuy-translator-toggle"
              aria-label="Open translator"
              title={language === 'en' ? 'Translator' : 'Traductor'}
              className="h-11 w-11 p-0 rounded-full shrink-0 flex items-center justify-center bg-muted text-muted-foreground hover:bg-caribbean/10 hover:text-caribbean transition-colors"
            >
              <Languages size={18} />
            </Link>
            <Input
              data-testid="chuy-chat-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder={t('chuy.placeholder')}
              className="rounded-full h-11 flex-1"
              disabled={!user || loading}
            />
            <Button
              type="submit"
              data-testid="chuy-chat-send"
              disabled={!user || loading || !input.trim()}
              className="h-11 w-11 p-0 rounded-full bg-caribbean hover:bg-caribbean/90 shrink-0"
            >
              {loading ? <Loader2 className="animate-spin" size={18} /> : <Send size={18} />}
            </Button>
          </form>
          {!user && (
            <p className="text-[10px] text-center text-muted-foreground mt-1">
              <Link to="/auth?next=/chuy-chat" className="text-caribbean font-semibold">
                {language === 'en' ? 'Sign in' : 'Inicia sesión'}
              </Link>{' '}
              {language === 'en' ? 'to chat with Chuy' : 'para chatear con Chuy'}
            </p>
          )}
        </div>
      </div>

      {/* Render bottom nav manually, OUTSIDE the height:100dvh wrapper, so it stays anchored */}
      <BottomNav />
    </MobileShell>
  );
}
